import { useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { selectMatterById } from '@/matter/store/selectors'
import { useAppSelector } from '@/store/store-hooks'
import { RootState } from '@/store/store'
import { selectTimelineAssetForMatter } from '@/timeline/asset/store/selectors'
import TimelineAssetChatButton from '@/timeline/asset/components/TimelineAssetChatButton'
import TimelineAssetDownloadButton from '@/timeline/asset/components/TimelineAssetDownloadButton'
import { matterPaths } from '@/routing/paths'
import { Header } from '@/components/header/Header'
import type { BreadcrumbItemType } from '@/components/breadcrumbs/BreadcrumbItem'

/**
 * Breadcrumbs for the living Timeline: Matter > Timeline, with Ask Assistant + Download
 * actions once the timeline has events.
 */
export default function MatterBreadcrumbsTimelineAsset({ showAddFilesCrumb = false }: { showAddFilesCrumb?: boolean } = {}) {
  const navigate = useNavigate()
  const { matterId } = useParams<{ matterId: string }>()

  const initialLoadComplete = useAppSelector((state: RootState) => state.mattersState.initialLoadComplete)
  const matter = useAppSelector((state: RootState) => selectMatterById(state, { matterId }))
  const asset = useAppSelector((state: RootState) => selectTimelineAssetForMatter(state, { matterId }))

  const breadcrumbItems: BreadcrumbItemType[] = useMemo(() => {
    if (!initialLoadComplete) return []

    const items: BreadcrumbItemType[] = []
    if (matter) {
      items.push({
        id: `matter-${matter.id}`,
        label: matter.name,
        onClick: () => navigate(matterPaths.overview(matter.id)),
      })
    }
    items.push({
      id: 'timeline',
      label: 'Timeline',
      // On the Add Files sub-page, Timeline becomes a clickable parent crumb.
      ...(showAddFilesCrumb && matterId ? { onClick: () => navigate(matterPaths.timeline.index(matterId)) } : {}),
    })
    if (showAddFilesCrumb) {
      items.push({ id: 'add-files', label: 'Add Files' })
    }
    return items
  }, [initialLoadComplete, matter, navigate, showAddFilesCrumb, matterId])

  const showActions = asset && asset.total_events > 0 && matterId

  return (
    <Header
      breadcrumbs={breadcrumbItems}
      endContent={
        showActions ? (
          <div className="hidden lg:flex items-center gap-3.5">
            <TimelineAssetChatButton assetId={asset.id} matterId={matterId} />
            <TimelineAssetDownloadButton assetId={asset.id} />
          </div>
        ) : undefined
      }
    />
  )
}
